import { ApolloClient, gql, makeVar, useApolloClient, useReactiveVar } from "@apollo/client";
import { stringify } from "csv-stringify/sync";
import download from "downloadjs";
import { useCallback } from "react";

import { useAppState } from "../../hooks/useAppState";
import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { getNotesPdfExportStatus, startNotesPdfExport } from "../../util/pdfService";
import { DeleteNote } from "./__generated__/DeleteNote";
import { Notes } from "./__generated__/Notes";
import { SaveNote } from "./__generated__/SaveNote";

export const SAVE_NOTE_MUTATION = gql`
  mutation SaveNote($instanceId: String!, $courseId: Int!, $slideId: String!, $contentBase64: String!) {
    saveNote(instanceId: $instanceId, courseId: $courseId, slideId: $slideId, contentBase64: $contentBase64) {
      slideId
      content
    }
  }
`;

export const DELETE_NOTE_MUTATION = gql`
  mutation DeleteNote($slideId: String!) {
    deleteNote(slideId: $slideId)
  }
`;

export const NOTES_QUERY = gql`
  query Notes($courseId: Int!) {
    course(courseId: $courseId) {
      trainingId
      sections {
        modules {
          slides {
            id
            fullNumber
            name
            notes {
              slideId
              content
            }
          }
        }
      }
    }
  }
`;

export interface Note {
  slideId: string;
  slideName: string;
  content: string;
}

const notesVar = makeVar<Note[]>([]);
const editingVar = makeVar<string | undefined>(undefined);
const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

const stringContent = (content: string) => {
  try {
    const jsonContent = JSON.parse(content) as { ops: { insert: string }[] };
    if (jsonContent.ops)
      return jsonContent.ops
        .map(op => op.insert)
        .join("")
        .trim();
    else throw new Error("Not quill delta");
  } catch (error) {
    console.error("Failed to parse content", error);
    return content;
  }
};

const notesAsRows = (client: ApolloClient<object>, courseId: number | null | undefined, toPlainText = false) => {
  const queryResponse = client.readQuery<Notes>({
    query: NOTES_QUERY,
    variables: { courseId },
  });

  if (!queryResponse) return [];

  const courseNotes: Note[] = [];

  for (const section of queryResponse.course.sections) {
    for (const module of section.modules) {
      for (const slide of module.slides) {
        if (!slide.notes) continue;
        courseNotes.push({
          slideId: slide.id,
          slideName: `${slide.fullNumber}: ${slide.name}`,
          content: toPlainText ? stringContent(slide.notes.content) : slide.notes.content,
        });
      }
    }
  }
  return courseNotes;
};

export function useNotes() {
  const {
    appState: { courseId, instanceId },
  } = useAppState();
  const { reportError } = useErrorHandler();

  const client = useApolloClient();

  const notes = useReactiveVar(notesVar);
  const editing = useReactiveVar(editingVar);

  const getNotes = useCallback(() => {
    notesVar(notesAsRows(client, courseId));
  }, [courseId, client]);

  const exportNotes = useCallback(
    async (downloadAsCSV: boolean): Promise<void> => {
      const exportType = downloadAsCSV ? "csv" : "pdf";

      createTelemetryEvent({
        feature: "notes",
        action: "export",
        action_meta: exportType,
      }).fire("telemetry");

      if (downloadAsCSV) {
        const rows = notesAsRows(client, courseId, true) ?? [];

        download(
          stringify(rows.filter(row => row.content).map(row => [row.slideName, row.content])),
          "notes.csv",
          "text/csv",
        );
        return;
      }

      if (!courseId) {
        await reportError(ErrorSeverity.LOW, "Error downloading notes", { reason: "Missing training ID" });
        return;
      }

      try {
        const { requestId } = await startNotesPdfExport(courseId);
        const maxAttempts = 20;
        const pollDelayMs = 2000;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const status = await getNotesPdfExportStatus(requestId);

          if (status.status === "complete" && status.url) {
            window.open(status.url, "_blank");
            return;
          }

          if (status.status === "error") {
            throw new Error(status.message || "Notes PDF generation failed");
          }

          if (attempt < maxAttempts - 1) {
            await sleep(pollDelayMs);
          }
        }

        throw new Error("Notes PDF generation timed out");
      } catch (error) {
        await reportError(ErrorSeverity.LOW, "Error exporting notes", { error });
      }
    },
    [courseId, client, reportError],
  );

  const saveNote = useCallback(
    async (slideId: string, content: string): Promise<void> => {
      createTelemetryEvent({
        feature: "notes",
        action: "save",
      }).fire("telemetry");
      await client.mutate<SaveNote>({
        mutation: SAVE_NOTE_MUTATION,
        variables: {
          instanceId,
          courseId,
          slideId,
          contentBase64: Buffer.from(content).toString("base64"),
        },
        update: (cache, { data }) => {
          if (data?.saveNote) {
            const slideCacheId = cache.identify({ __typename: "Slide", id: slideId });

            if (slideCacheId) {
              cache.modify({
                id: slideCacheId,
                fields: {
                  notes(cachedNotes) {
                    if (cachedNotes) {
                      return { ...cachedNotes, ...data.saveNote };
                    }
                    return { ...data.saveNote, updated: "" };
                  },
                },
              });
              const slideIds = notes.map(note => note.slideId);
              if (!slideIds.includes(slideId)) {
                createTelemetryEvent({
                  feature: "notes",
                  action: "add",
                }).fire("telemetry");
              }
            }
          }
        },
      });
      getNotes();
    },
    [instanceId, courseId, client, getNotes, notes],
  );

  const deleteNote = useCallback(
    async (slideId: string): Promise<void> => {
      createTelemetryEvent({
        feature: "notes",
        action: "delete",
      }).fire("telemetry");
      await client.mutate<DeleteNote>({
        mutation: DELETE_NOTE_MUTATION,
        variables: { slideId },
        update: (cache, { data }) => {
          if (data?.deleteNote) {
            const slideCacheId = cache.identify({ __typename: "Slide", id: slideId });
            if (slideCacheId) {
              cache.modify({
                id: slideCacheId,
                fields: {
                  notes: () => null,
                },
              });
            }
            const cacheId = cache.identify({ __typename: "Notes", slideId });
            if (cacheId) {
              cache.evict({ id: cacheId });
              cache.gc();
            }
          }
        },
      });
      getNotes();
    },
    [client, getNotes],
  );

  const editNote = useCallback((slideId?: string): void => {
    editingVar(slideId);
    createTelemetryEvent({
      feature: "notes",
      action: "edit",
    }).fire("telemetry");
  }, []);

  return { exportNotes, saveNote, deleteNote, notes, editNote, editing, getNotes };
}
